import { Injectable, inject } from '@angular/core'

import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'

import { filter, map, withLatestFrom } from 'rxjs'

import { ConversationActions } from '@flowx/designer/modules/ai/conversation/conversation.actions'

// eslint-disable-next-line import/no-cycle
import { AiSelectors } from './ai.selectors'

import { AiAnalystActions } from '../agents/analyst/store/analyst.actions'
import { AiDesignerActions } from '../agents/designer/store/designer.actions'

/**
 * Bridges the canvas Keep/Discard buttons to the LIVE new-ai keep/discard
 * actions (2026-06-24).
 *
 * The blue draft container's small Keep/Discard buttons dispatch
 * `ConversationActions.{keep,discard}OutcomeClick`. The (old) `conversation.effects`
 * routes those to `modules/ai` agent actions — but the app runs `new-ai`
 * (`app.config.ts` wires only `new-ai/store/ai-store.module`; the old
 * `modules/ai` store is never registered). So those clicks dispatch into a dead
 * store and the canvas buttons no-op for EVERY new-ai-rendered draft — bpmn AND
 * ui, native "Generate with AI" AND FlowX Code. (Pre-existing Designer bug,
 * surfaced by FlowX Code.)
 *
 * This bridge re-routes the same clicks to the new-ai keep/discard based on
 * which draft is currently present (process entities → analyst; generated UI →
 * designer), and feeds the keep/discard SUCCESS back into the conversation
 * state machine so `keepDiscardOutcomeState` returns to idle (buttons re-enable).
 *
 * Additive by design: it does NOT touch the old `conversation.effects` (whose
 * dead old-ai dispatch stays a harmless no-op). Only acts when a process/ui
 * draft exists, so other agents (developer/assistant/etc.) are unaffected.
 */
@Injectable()
export class ConversationKeepDiscardBridgeEffects {
  readonly #actions$ = inject(Actions)
  readonly #store = inject(Store)

  keepOutcome = createEffect(() =>
    this.#actions$.pipe(
      ofType(ConversationActions.keepOutcomeClick),
      withLatestFrom(
        this.#store.select(AiSelectors.generatedProcessEntities),
        this.#store.select(AiSelectors.generatedUINew)
      ),
      filter(([, processEntities, generatedUINew]) => !!processEntities || !!generatedUINew),
      map(([, processEntities]) =>
        processEntities ? AiAnalystActions.keepProcessDraft() : AiDesignerActions.keepUiDraft()
      )
    )
  )

  discardOutcome = createEffect(() =>
    this.#actions$.pipe(
      ofType(ConversationActions.discardOutcomeClick),
      withLatestFrom(
        this.#store.select(AiSelectors.generatedProcessEntities),
        this.#store.select(AiSelectors.generatedUINew)
      ),
      filter(([, processEntities, generatedUINew]) => !!processEntities || !!generatedUINew),
      map(([, processEntities]) =>
        processEntities
          ? AiAnalystActions.discardProcessDraft()
          : AiDesignerActions.discardUiDraft()
      )
    )
  )

  // Reset the conversation keep/discard state machine when the new-ai
  // keep/discard completes, so the buttons leave their "keeping"/"discarding"
  // disabled state.
  keepDone = createEffect(() =>
    this.#actions$.pipe(
      ofType(
        AiAnalystActions.keepProcessDraftSuccess,
        AiDesignerActions.keepTemplateConfigDraftSuccess
      ),
      map(() => ConversationActions.keepOutcomeSuccess())
    )
  )

  discardDone = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiAnalystActions.discardProcessDraftSuccess, AiDesignerActions.discardUiDraftSuccess),
      map(() => ConversationActions.discardOutcomeSuccess())
    )
  )
}
